import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertCircle,
  Camera,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  displayToCents,
  normalizeBarcode,
  parseLocalePrice,
  useTranslation,
} from '@dukanti/shared';
import type {
  CreateProductInput,
  CategoryErrorKey,
  ProductMode,
  ProductOptionInput,
  ProductErrorKey,
  ProductImageWithUrl,
  ProductVariantInput,
  StoreCategoryWithMedia,
  TranslationKey,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { BarcodeCameraScanDialog } from '@/features/stock/components';
import { useNavBlocker, useNavBlockerMarkSettled } from '@/hooks/use-nav-blocker';
import { createProductFormSchema, type ProductFormValues } from '../lib/product-form-schema';
import { MultiImageUpload } from './multi-image-upload';
import type { MultiImageUploadSlot } from './multi-image-upload-types';
import { PriceInput } from './price-input';

/**
 * Shared create/edit product form (F-003).
 *
 * Used by two routes:
 *   - `product-new-route`  (mode='create') — submits via useCreateProduct
 *   - `product-edit-route` (mode='edit')   — F4c-4 will wire useUpdateProduct
 *
 * The form is a "dumb" component: it owns RHF state, renders the
 * fields + validation, and calls `onSubmit` / `onCancel` props. The
 * parent route owns the mutation and redirect.
 *
 * Why a shared component: every field, Zod rule, i18n key, and RTL
 * tweak must be identical across create/edit. Duplicating would risk
 * drift between the two flows (and double-maintenance for the bidi /
 * price parsing quirks).
 */
export interface ProductFormInitialValues {
  name?: string;
  description?: string;
  /** Display price — the edit route converts cents → display before
   *  passing this in via `centsToDisplay`. */
  priceRaw?: string;
  saleEnabled?: boolean;
  salePriceRaw?: string;
  inventory?: number;
  categoryId?: string;
  /** Existing images when editing. Create mode passes `[]`. */
  images?: ProductImageWithUrl[];
  mode?: ProductMode;
  options?: ProductFormValues['options'];
  variants?: Array<
    Omit<Partial<ProductFormValues['variants'][number]>, 'optionValueIds'> & {
      optionValueIds?: string[];
    }
  >;
  prefillBarcode?: string;
  deletedReuseConfirmedNormalized?: string | null;
  barcodeValue?: string;
  barcodeReplaceConfirmed?: boolean;
  barcodeClearConfirmed?: boolean;
  barcodeDeletedReuseConfirmedNormalized?: string | null;
}

export interface ProductFormSubmitPayload {
  /** Ready to hand straight to `createProduct` / `updateProduct`. */
  input: Omit<CreateProductInput, 'store_id'>;
  /** Variant RPC-ready product mode. */
  mode: ProductMode;
  /** Variant RPC-ready option groups. */
  options: ProductOptionInput[];
  /** Variant RPC-ready sellable rows. */
  variants: ProductVariantInput[];
  /** Newly staged Files (ordered). */
  newFiles: File[];
  /** For the edit form: existing images still kept, in their new order. */
  keptExistingImageIds: string[];
  /** For the edit form: existing image ids the user removed. */
  removedExistingImageIds: string[];
  /** Full ordered slot list — consumers that need the full state can
   *  use this to drive the per-slot status animations. */
  slots: MultiImageUploadSlot[];
}

export interface ProductFormProps {
  mode: 'create' | 'edit';
  /** Required for edit mode; optional for create (we default to empty). */
  initialValues?: ProductFormInitialValues;
  initialBarcode?: string;
  /** Called with normalized payload after Zod passes. Async so the
   *  caller can await the mutation and surface errors. */
  onSubmit: (payload: ProductFormSubmitPayload) => Promise<void>;
  /** Called when the user hits cancel (or the back link). */
  onCancel?: () => void;
  /** Submit-level error (already mapped to a translation key). */
  submitError?: ProductErrorKey | CategoryErrorKey | null;
  /** True while the mutation is pending. */
  isSubmitting?: boolean;
  /** Active store categories available for assignment. */
  categories?: readonly (
    | StoreCategoryWithMedia
    | { readonly id: string; readonly name: string; readonly image_url?: string | null }
  )[];
  /** True while the parent route is loading categories. */
  isLoadingCategories?: boolean;
}

const EMPTY_INITIAL: ProductFormInitialValues = {
  name: '',
  description: '',
  priceRaw: '',
  saleEnabled: false,
  salePriceRaw: '',
  inventory: 0,
  categoryId: '',
  images: [],
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const VARIANT_TEMPLATE_IDS = [
  'sizes',
  'colors',
  'sizeColor',
  'packageQuantity',
  'supplement',
  'volumeWeight',
  'custom',
] as const;

type VariantTemplateId = (typeof VARIANT_TEMPLATE_IDS)[number];
type ProductFormT = ReturnType<typeof useTranslation>['t'];
type TemplateOptionBlueprint = {
  readonly name: string;
  readonly values: readonly string[];
};

function makeClientId(): string {
  return crypto.randomUUID();
}

function realUuid(id: string | undefined): string | undefined {
  return id && UUID_RE.test(id) ? id : undefined;
}

function makeTemplateOption(
  name: string,
  values: readonly string[],
  displayOrder: number,
): ProductFormValues['options'][number] {
  return {
    id: makeClientId(),
    name,
    displayOrder,
    values: values.map((value, valueIndex) => ({
      id: makeClientId(),
      value,
      displayOrder: valueIndex,
    })),
  };
}

function getVariantTemplateBlueprint(
  templateId: VariantTemplateId,
  t: ProductFormT,
): readonly TemplateOptionBlueprint[] {
  switch (templateId) {
    case 'sizes':
      return [
        {
          name: t('admin.products.form.templates.sizes.optionName'),
          values: [
            t('admin.products.form.templates.sizes.valueSmall'),
            t('admin.products.form.templates.sizes.valueMedium'),
            t('admin.products.form.templates.sizes.valueLarge'),
          ],
        },
      ];
    case 'colors':
      return [
        {
          name: t('admin.products.form.templates.colors.optionName'),
          values: [
            t('admin.products.form.templates.colors.valueBlack'),
            t('admin.products.form.templates.colors.valueWhite'),
            t('admin.products.form.templates.colors.valueGreen'),
          ],
        },
      ];
    case 'sizeColor':
      return [
        {
          name: t('admin.products.form.templates.sizes.optionName'),
          values: [
            t('admin.products.form.templates.sizes.valueSmall'),
            t('admin.products.form.templates.sizes.valueMedium'),
            t('admin.products.form.templates.sizes.valueLarge'),
          ],
        },
        {
          name: t('admin.products.form.templates.colors.optionName'),
          values: [
            t('admin.products.form.templates.colors.valueBlack'),
            t('admin.products.form.templates.colors.valueWhite'),
          ],
        },
      ];
    case 'packageQuantity':
      return [
        {
          name: t('admin.products.form.templates.packageQuantity.optionName'),
          values: [
            t('admin.products.form.templates.packageQuantity.valueSingle'),
            t('admin.products.form.templates.packageQuantity.valuePack6'),
            t('admin.products.form.templates.packageQuantity.valuePack12'),
          ],
        },
      ];
    case 'supplement':
      return [
        {
          name: t('admin.products.form.templates.supplement.strengthOptionName'),
          values: [
            t('admin.products.form.templates.supplement.value500mg'),
            t('admin.products.form.templates.supplement.value1000mg'),
          ],
        },
        {
          name: t('admin.products.form.templates.supplement.packageOptionName'),
          values: [
            t('admin.products.form.templates.supplement.value30Capsules'),
            t('admin.products.form.templates.supplement.value60Capsules'),
          ],
        },
      ];
    case 'volumeWeight':
      return [
        {
          name: t('admin.products.form.templates.volumeWeight.optionName'),
          values: [
            t('admin.products.form.templates.volumeWeight.value250ml'),
            t('admin.products.form.templates.volumeWeight.value500ml'),
            t('admin.products.form.templates.volumeWeight.value1kg'),
          ],
        },
      ];
    case 'custom':
      return [
        {
          name: t('admin.products.form.templates.custom.optionName'),
          values: [t('admin.products.form.templates.custom.valueOne')],
        },
      ];
  }
}

function makeTemplateOptions(
  templateId: VariantTemplateId,
  t: ProductFormT,
): ProductFormValues['options'] {
  return getVariantTemplateBlueprint(templateId, t).map((option, optionIndex) =>
    makeTemplateOption(option.name, option.values, optionIndex),
  );
}

function optionsMatchTemplate(
  options: ProductFormValues['options'] | undefined,
  blueprint: readonly TemplateOptionBlueprint[],
): boolean {
  if (!options || options.length !== blueprint.length) return false;

  return blueprint.every((expectedOption, optionIndex) => {
    const actualOption = options[optionIndex];
    if (!actualOption || actualOption.name.trim() !== expectedOption.name) return false;

    const actualValues = actualOption.values.map((value) => value.value.trim());
    return (
      actualValues.length === expectedOption.values.length &&
      expectedOption.values.every(
        (expectedValue, valueIndex) => actualValues[valueIndex] === expectedValue,
      )
    );
  });
}

function buildOptionSummary(
  options: ProductFormValues['options'],
  valueIds: readonly string[],
): string {
  return valueIds
    .map((valueId) => {
      const option = options.find((candidate) =>
        candidate.values.some((value) => value.id === valueId),
      );
      const value = option?.values.find((candidate) => candidate.id === valueId);
      if (!option || !value) return null;
      return `${option.name.trim()}: ${value.value.trim()}`;
    })
    .filter((part): part is string => Boolean(part))
    .join(' / ');
}

function cartesianProduct<T>(groups: readonly (readonly T[])[]): T[][] {
  return groups.reduce<T[][]>(
    (acc, group) => acc.flatMap((prefix) => group.map((item) => [...prefix, item])),
    [[]],
  );
}

function buildVariantRows(
  options: ProductFormValues['options'],
  previousRows: ProductFormValues['variants'],
  fallback: Pick<ProductFormValues, 'priceRaw' | 'saleEnabled' | 'salePriceRaw' | 'inventory'>,
): ProductFormValues['variants'] {
  const usableOptions = options
    .map((option) => ({
      ...option,
      values: option.values.filter((value) => value.id && value.value.trim().length > 0),
    }))
    .filter((option) => option.name.trim().length > 0 && option.values.length > 0);

  if (usableOptions.length === 0) return [];

  const previousBySignature = new Map(
    previousRows.map((row) => [[...row.optionValueIds].sort().join('|'), row]),
  );

  return cartesianProduct(usableOptions.map((option) => option.values)).map((values, index) => {
    const optionValueIds = values.map((value) => value.id).filter((id): id is string => !!id);
    const signature = [...optionValueIds].sort().join('|');
    const existing = previousBySignature.get(signature);

    return {
      id: existing?.id ?? makeClientId(),
      optionValueIds,
      optionSummary: buildOptionSummary(usableOptions, optionValueIds),
      priceRaw: existing?.priceRaw ?? fallback.priceRaw,
      saleEnabled: existing?.saleEnabled ?? fallback.saleEnabled,
      salePriceRaw: existing?.salePriceRaw ?? fallback.salePriceRaw,
      inventory: existing?.inventory ?? fallback.inventory,
      active: existing?.active ?? true,
      isDefault: existing?.isDefault ?? index === 0,
      imageId: existing?.imageId ?? null,
      barcodeValue: existing?.barcodeValue ?? '',
      previousBarcodeNormalized: existing?.previousBarcodeNormalized ?? null,
      barcodeReplaceConfirmed: existing?.barcodeReplaceConfirmed ?? false,
      barcodeClearConfirmed: existing?.barcodeClearConfirmed ?? false,
      barcodeDeletedReuseConfirmedNormalized:
        existing?.barcodeDeletedReuseConfirmedNormalized ?? null,
    };
  });
}

export function ProductForm({
  mode,
  initialValues,
  initialBarcode,
  onSubmit,
  onCancel,
  submitError,
  isSubmitting = false,
  categories = [],
  isLoadingCategories = false,
}: ProductFormProps) {
  const { t, dir, lang } = useTranslation();

  // Build the Zod resolver fresh when the locale changes so
  // `parseLocalePrice` reads the current lang. react-hook-form
  // reads `resolver` by reference, so memoizing is important.
  const schema = useMemo(() => createProductFormSchema(lang), [lang]);

  const defaults = useMemo<ProductFormValues>(() => {
    const seed = initialValues ?? EMPTY_INITIAL;
    const initialVariants = seed.variants?.length
      ? seed.variants
      : [
          {
            id: undefined,
            optionValueIds: [],
            optionSummary: '',
            priceRaw: seed.priceRaw ?? '',
            saleEnabled: seed.saleEnabled ?? false,
            salePriceRaw: seed.salePriceRaw ?? '',
            inventory: seed.inventory ?? 0,
            active: true,
            isDefault: true,
            imageId: null,
            barcodeValue: initialBarcode ?? seed.prefillBarcode ?? seed.barcodeValue ?? '',
            previousBarcodeNormalized: null,
            barcodeReplaceConfirmed: false,
            barcodeClearConfirmed: false,
            barcodeDeletedReuseConfirmedNormalized: seed.deletedReuseConfirmedNormalized ?? null,
          },
        ];
    return {
      name: seed.name ?? '',
      description: seed.description ?? '',
      priceRaw: seed.priceRaw ?? '',
      saleEnabled: seed.saleEnabled ?? false,
      salePriceRaw: seed.salePriceRaw ?? '',
      inventory: seed.inventory ?? 0,
      categoryId: seed.categoryId ?? '',
      images: [], // RHF only tracks newly picked Files; full slots
      // live in local state below.
      mode: seed.mode ?? 'simple',
      options: seed.options ?? [],
      variants: initialVariants.map((variant, index) => ({
        id: variant.id,
        optionValueIds: variant.optionValueIds ?? [],
        optionSummary: variant.optionSummary ?? '',
        priceRaw: variant.priceRaw ?? seed.priceRaw ?? '',
        saleEnabled: variant.saleEnabled ?? seed.saleEnabled ?? false,
        salePriceRaw: variant.salePriceRaw ?? seed.salePriceRaw ?? '',
        inventory: variant.inventory ?? seed.inventory ?? 0,
        active: variant.active ?? true,
        isDefault: variant.isDefault ?? index === 0,
        imageId: variant.imageId ?? null,
        barcodeValue: variant.barcodeValue ?? '',
        previousBarcodeNormalized: variant.previousBarcodeNormalized ?? null,
        barcodeReplaceConfirmed: variant.barcodeReplaceConfirmed ?? false,
        barcodeClearConfirmed: variant.barcodeClearConfirmed ?? false,
        barcodeDeletedReuseConfirmedNormalized:
          variant.barcodeDeletedReuseConfirmedNormalized ?? null,
      })),
    };
  }, [initialBarcode, initialValues]);

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ProductFormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: defaults,
  });

  const {
    fields: optionFields,
    append: appendOption,
    remove: removeOption,
  } = useFieldArray({
    control,
    name: 'options',
    keyName: 'fieldId',
  });

  const {
    fields: variantFields,
    replace: replaceVariants,
    update: updateVariant,
  } = useFieldArray({
    control,
    name: 'variants',
    keyName: 'fieldId',
  });

  // --- Slot state (images) ----------------------------------------
  // Kept outside RHF because:
  //  - Files can't be cloned through RHF's structured-clone path
  //    without losing type (`File instanceof File` fails after clone
  //    in some environments).
  //  - `kind: 'existing'` slots have no File, only a ProductImage.
  //  - Ordering + per-slot status are parent concerns, not field
  //    concerns, so they belong in a `useState`.
  const [slots, setSlots] = useState<MultiImageUploadSlot[]>(() =>
    (initialValues?.images ?? []).map((img) => ({
      key: `existing-${img.id}`,
      kind: 'existing' as const,
      previewUrl: img.url,
      status: 'idle' as const,
      existingImage: img,
    })),
  );
  const existingImageOptions = useMemo(
    () =>
      slots
        .filter(
          (
            slot,
          ): slot is MultiImageUploadSlot & {
            readonly kind: 'existing';
            readonly existingImage: ProductImageWithUrl;
          } => slot.kind === 'existing' && Boolean(slot.existingImage),
        )
        .map((slot, index) => ({
          id: slot.existingImage.id,
          label: t('admin.products.form.imagePosition', { current: index + 1 }),
        })),
    [slots, t],
  );

  const handleSlotsChange = useCallback((next: MultiImageUploadSlot[]) => {
    setSlots(next);
  }, []);

  const originalExistingIds = useMemo(
    () => new Set((initialValues?.images ?? []).map((img) => img.id)),
    [initialValues],
  );
  const [barcodeScannerTarget, setBarcodeScannerTarget] = useState<
    { readonly type: 'simple' } | { readonly type: 'variant'; readonly index: number } | null
  >(null);

  // F-027 §4.2.1 (C1) — image slots live outside RHF so `formState.isDirty`
  // does NOT reflect add/remove/reorder. Snapshot the initial slot key set
  // ONCE per mount of this form and compare order-sensitively. A pure
  // reorder still counts as dirty because it's a real edit the merchant
  // should not lose silently.
  const initialSlotKeysRef = useRef<string[]>(
    (initialValues?.images ?? []).map((img) => `existing-${img.id}`),
  );

  const slotsDirty = useMemo(() => {
    const initial = initialSlotKeysRef.current;
    if (slots.length !== initial.length) return true;
    if (slots.some((s) => s.kind === 'new')) return true;
    const currentKeys = slots.map((s) =>
      s.kind === 'existing' && s.existingImage ? `existing-${s.existingImage.id}` : `new-${s.key}`,
    );
    for (let i = 0; i < initial.length; i++) {
      if (currentKeys[i] !== initial[i]) return true;
    }
    return false;
  }, [slots]);

  // Publish dirty state upward so the shell's NavBlocker intercepts any
  // intra-shell navigation while there are unsaved edits (scalar fields
  // OR image slot changes). On unmount the hook's cleanup effect clears
  // both isDirty and slotsDirty.
  useNavBlocker(isDirty || slotsDirty);

  // F-027 Phase 4.5 FIX 1 — reset-before-navigate escape valve. The
  // parent route's onSubmit handler awaits the mutation and calls
  // navigate() on the same tick. At the moment navigate() fires, RHF's
  // isDirty is still true (reset happens on the next render) and the
  // slots array is still whatever the merchant picked, so useBlocker
  // would normally intercept the programmatic post-submit nav. We call
  // markSettled() BEFORE awaiting onSubmit to flip the provider's
  // internal isDirtyRef synchronously — the very next navigation attempt
  // then sees `false` and flows through. On error we do nothing special:
  // the next render re-publishes RHF's still-true isDirty via the effect
  // in useNavBlocker, re-engaging the blocker for any subsequent
  // sidebar-click or browser-back navigation.
  const markSettled = useNavBlockerMarkSettled();
  const saleEnabled = useWatch({ control, name: 'saleEnabled' });
  const productMode = useWatch({ control, name: 'mode' });
  const optionValues = useWatch({ control, name: 'options' });
  const variantRows = useWatch({ control, name: 'variants' });
  const variantsRootError = (
    errors.variants as
      | (typeof errors.variants & {
          readonly root?: { readonly message?: string };
          readonly message?: string;
        })
      | undefined
  )?.root?.message;
  const variantsArrayError =
    variantsRootError ??
    (
      errors.variants as
        | (typeof errors.variants & {
            readonly message?: string;
          })
        | undefined
    )?.message;
  const basePriceRaw = useWatch({ control, name: 'priceRaw' });
  const baseSaleEnabled = saleEnabled;
  const baseSalePriceRaw = useWatch({ control, name: 'salePriceRaw' });
  const baseInventory = useWatch({ control, name: 'inventory' });
  const activeTemplateId = useMemo<VariantTemplateId | null>(() => {
    if (productMode !== 'options') return null;
    const builtInTemplateId = VARIANT_TEMPLATE_IDS.filter(
      (templateId) => templateId !== 'custom',
    ).find((templateId) =>
      optionsMatchTemplate(optionValues, getVariantTemplateBlueprint(templateId, t)),
    );
    if (builtInTemplateId) return builtInTemplateId;

    return optionValues && optionValues.length > 0 ? 'custom' : null;
  }, [optionValues, productMode, t]);

  useEffect(() => {
    if (productMode !== 'simple') return;
    const current = getValues('variants.0');
    setValue(
      'variants.0',
      {
        id: current?.id,
        optionValueIds: [],
        optionSummary: '',
        priceRaw: basePriceRaw,
        saleEnabled: baseSaleEnabled,
        salePriceRaw: baseSalePriceRaw,
        inventory: baseInventory,
        active: true,
        isDefault: true,
        imageId: null,
        barcodeValue: current?.barcodeValue ?? '',
        previousBarcodeNormalized: current?.previousBarcodeNormalized ?? null,
        barcodeReplaceConfirmed: current?.barcodeReplaceConfirmed ?? false,
        barcodeClearConfirmed: current?.barcodeClearConfirmed ?? false,
        barcodeDeletedReuseConfirmedNormalized:
          current?.barcodeDeletedReuseConfirmedNormalized ?? null,
      },
      { shouldValidate: false },
    );
  }, [
    baseInventory,
    basePriceRaw,
    baseSaleEnabled,
    baseSalePriceRaw,
    getValues,
    productMode,
    setValue,
  ]);

  useEffect(() => {
    if (productMode !== 'options') return;

    const nextRows = buildVariantRows(optionValues ?? [], variantRows ?? [], {
      priceRaw: basePriceRaw,
      saleEnabled: baseSaleEnabled,
      salePriceRaw: baseSalePriceRaw,
      inventory: baseInventory,
    });

    const currentSignature = (variantRows ?? [])
      .map((variant) => `${(variant.optionValueIds ?? []).join('|')}::${variant.optionSummary}`)
      .join(';;');
    const nextSignature = nextRows
      .map((variant) => `${(variant.optionValueIds ?? []).join('|')}::${variant.optionSummary}`)
      .join(';;');

    if (currentSignature !== nextSignature) {
      replaceVariants(nextRows);
    }
  }, [
    baseInventory,
    basePriceRaw,
    baseSaleEnabled,
    baseSalePriceRaw,
    optionValues,
    productMode,
    replaceVariants,
    variantRows,
  ]);

  const setProductMode = useCallback(
    (nextMode: ProductMode) => {
      setValue('mode', nextMode, { shouldDirty: true, shouldValidate: true });
      if (nextMode === 'simple') {
        setValue('options', [], { shouldDirty: true, shouldValidate: true });
        replaceVariants([]);
      } else if (getValues('options').length === 0) {
        appendOption({
          id: makeClientId(),
          name: '',
          displayOrder: 0,
          values: [{ id: makeClientId(), value: '', displayOrder: 0 }],
        });
      }
    },
    [appendOption, getValues, replaceVariants, setValue],
  );

  const applyVariantTemplate = useCallback(
    (templateId: VariantTemplateId) => {
      setValue('mode', 'options', { shouldDirty: true, shouldValidate: true });

      if (templateId === 'custom') {
        const currentOptions = getValues('options');
        const matchesBuiltInTemplate = VARIANT_TEMPLATE_IDS.filter(
          (candidateId) => candidateId !== 'custom',
        ).some((candidateId) =>
          optionsMatchTemplate(currentOptions, getVariantTemplateBlueprint(candidateId, t)),
        );

        if (currentOptions.length === 0 || matchesBuiltInTemplate) {
          setValue(
            'options',
            [
              {
                id: makeClientId(),
                name: '',
                displayOrder: 0,
                values: [{ id: makeClientId(), value: '', displayOrder: 0 }],
              },
            ],
            { shouldDirty: true, shouldValidate: true },
          );
          replaceVariants([]);
        }
        return;
      }

      const templateOptions = makeTemplateOptions(templateId, t);

      const nextVariants = buildVariantRows(templateOptions, getValues('variants') ?? [], {
        priceRaw: getValues('priceRaw'),
        saleEnabled: getValues('saleEnabled'),
        salePriceRaw: getValues('salePriceRaw'),
        inventory: getValues('inventory'),
      });

      setValue('options', templateOptions, { shouldDirty: true, shouldValidate: true });
      replaceVariants(nextVariants);
    },
    [getValues, replaceVariants, setValue, t],
  );

  const addOption = useCallback(() => {
    appendOption({
      id: makeClientId(),
      name: '',
      displayOrder: optionFields.length,
      values: [{ id: makeClientId(), value: '', displayOrder: 0 }],
    });
  }, [appendOption, optionFields.length]);

  const addOptionValue = useCallback(
    (optionIndex: number) => {
      const values = getValues(`options.${optionIndex}.values`) ?? [];
      setValue(
        `options.${optionIndex}.values`,
        [
          ...values,
          {
            id: makeClientId(),
            value: '',
            displayOrder: values.length,
          },
        ],
        { shouldDirty: true, shouldValidate: true },
      );
    },
    [getValues, setValue],
  );

  const removeOptionValue = useCallback(
    (optionIndex: number, valueIndex: number) => {
      const values = getValues(`options.${optionIndex}.values`) ?? [];
      setValue(
        `options.${optionIndex}.values`,
        values
          .filter((_, index) => index !== valueIndex)
          .map((value, index) => ({ ...value, displayOrder: index })),
        { shouldDirty: true, shouldValidate: true },
      );
    },
    [getValues, setValue],
  );

  const internalSubmit = handleSubmit(async (values) => {
    // Convert priceRaw → cents via the same parser Zod used. Zod has
    // already confirmed the string parses, is positive, 2-decimal,
    // and in-range, so `displayToCents` just does the rounding.
    const parsed = parseLocalePrice(values.priceRaw, lang);
    const priceCents = displayToCents(parsed);
    const salePriceCents = values.saleEnabled
      ? displayToCents(parseLocalePrice(values.salePriceRaw, lang))
      : null;

    const newFiles = slots.filter((s) => s.kind === 'new' && s.file).map((s) => s.file as File);

    const keptExistingImageIds = slots
      .filter((s) => s.kind === 'existing' && s.existingImage)
      .map((s) => s.existingImage!.id);

    const removedExistingImageIds = Array.from(originalExistingIds).filter(
      (id) => !keptExistingImageIds.includes(id),
    );

    const input: ProductFormSubmitPayload['input'] = {
      name: values.name.trim(),
      description: values.description?.trim() ? values.description.trim() : null,
      price_cents: priceCents,
      sale_price_cents: salePriceCents,
      currency: 'ILS',
      inventory: values.inventory,
      category_id: values.categoryId,
    };

    const normalizedOptions: ProductOptionInput[] =
      values.mode === 'options'
        ? values.options.map((option, optionIndex) => ({
            ...(realUuid(option.id) ? { id: realUuid(option.id) } : {}),
            name: option.name.trim(),
            display_order: optionIndex,
            values: option.values.map((value, valueIndex) => ({
              ...(realUuid(value.id) ? { id: realUuid(value.id) } : {}),
              value: value.value.trim(),
              display_order: valueIndex,
            })),
          }))
        : [];

    const activeDefaultIndex = values.variants.findIndex(
      (variant) => variant.active && variant.isDefault,
    );
    const fallbackDefaultIndex = Math.max(
      0,
      values.variants.findIndex((variant) => variant.active),
    );

    const keptExistingImageIdSet = new Set(keptExistingImageIds);
    const normalizedVariants: ProductVariantInput[] = values.variants.map((variant, index) => {
      const variantPriceCents = displayToCents(parseLocalePrice(variant.priceRaw, lang));
      const variantSalePriceCents = variant.saleEnabled
        ? displayToCents(parseLocalePrice(variant.salePriceRaw, lang))
        : null;
      const imageId =
        variant.imageId && keptExistingImageIdSet.has(variant.imageId) ? variant.imageId : null;

      return {
        ...(realUuid(variant.id) ? { id: realUuid(variant.id) } : {}),
        price_cents: variantPriceCents,
        sale_price_cents: variantSalePriceCents,
        currency: 'ILS',
        inventory: variant.inventory,
        active: variant.active,
        is_default:
          activeDefaultIndex >= 0
            ? index === activeDefaultIndex
            : variant.active && index === fallbackDefaultIndex,
        image_id: imageId,
        display_order: index,
        option_value_ids: variant.optionValueIds
          .map((valueId) => realUuid(valueId))
          .filter((valueId): valueId is string => Boolean(valueId)),
        barcode_value: normalizeBarcode(variant.barcodeValue ?? '') || null,
        previous_barcode_normalized: variant.previousBarcodeNormalized || null,
        barcode_replace_confirmed: variant.barcodeReplaceConfirmed,
        barcode_clear_confirmed: variant.barcodeClearConfirmed,
        barcode_deleted_reuse_confirmed_normalized:
          variant.barcodeDeletedReuseConfirmedNormalized || null,
      };
    });

    // FIX 1 — mark the blocker "settled" synchronously before the parent
    // route's onSubmit handler awaits the mutation and calls navigate().
    // This writes to the NavBlockerProvider's isDirtyRef immediately so
    // the very next navigation attempt sees a false predicate. See
    // use-nav-blocker.tsx for why this is a ref and not state.
    markSettled();

    await onSubmit({
      input,
      mode: values.mode,
      options: normalizedOptions,
      variants: normalizedVariants,
      newFiles,
      keptExistingImageIds,
      removedExistingImageIds,
      slots,
    });
  });

  const titleKey: TranslationKey =
    mode === 'create' ? 'admin.products.form.title.new' : 'admin.products.form.title.edit';
  const submitLabelKey: TranslationKey =
    mode === 'create' ? 'admin.products.form.submitCreate' : 'admin.products.form.submitEdit';
  const simpleBarcodeErrorKey = errors.variants?.[0]?.barcodeValue?.message as
    | TranslationKey
    | undefined;
  const simpleBarcodeNeedsConfirmation =
    simpleBarcodeErrorKey === 'admin.products.errors.barcodeReplaceConfirmationRequired' ||
    simpleBarcodeErrorKey === 'admin.products.errors.barcodeClearConfirmationRequired';
  const barcodeErrorNeedsConfirmation = (key: string | undefined): boolean =>
    key === 'admin.products.errors.barcodeReplaceConfirmationRequired' ||
    key === 'admin.products.errors.barcodeClearConfirmationRequired';
  const submitErrorNeedsBarcodeConfirmation =
    submitError === 'admin.products.errors.barcodeReplaceConfirmationRequired' ||
    submitError === 'admin.products.errors.barcodeClearConfirmationRequired';
  const simpleBarcodeRow = variantRows?.[0];
  const confirmVariantBarcodeChange = (variantIndex: number) => {
    setValue(`variants.${variantIndex}.barcodeReplaceConfirmed`, true, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(`variants.${variantIndex}.barcodeClearConfirmed`, true, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  const confirmSimpleBarcodeChange = () => confirmVariantBarcodeChange(0);
  const applyScannedBarcode = (barcode: string) => {
    const variantIndex = barcodeScannerTarget?.type === 'variant' ? barcodeScannerTarget.index : 0;
    setValue(`variants.${variantIndex}.barcodeValue`, barcode, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(`variants.${variantIndex}.barcodeReplaceConfirmed`, false, {
      shouldDirty: true,
    });
    setValue(`variants.${variantIndex}.barcodeClearConfirmed`, false, {
      shouldDirty: true,
    });
    setValue(`variants.${variantIndex}.barcodeDeletedReuseConfirmedNormalized`, null, {
      shouldDirty: true,
    });
  };

  return (
    <form
      noValidate
      dir={dir}
      onSubmit={internalSubmit}
      className="mx-auto max-w-screen-md px-6 pb-32 pt-8"
      data-testid="product-form"
      aria-busy={isSubmitting}
    >
      {/* Back link — chevron flips via rtl:rotate-180 so the arrow
          always points "back" in the user's reading direction. */}
      <Link
        to="/products"
        className="text-zaatar mb-6 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
        data-testid="product-form-back"
      >
        <ChevronRight size={16} className="rtl:rotate-180" aria-hidden strokeWidth={2} />
        {t('admin.products.form.back')}
      </Link>

      <h1
        className="font-display text-foreground mb-8 text-start text-3xl tracking-tight"
        data-testid="product-form-title"
      >
        {t(titleKey)}
      </h1>

      <div className="border-border bg-card shadow-admin-card rounded-lg border p-6 sm:p-8">
        {submitError ? (
          <div
            role="alert"
            data-testid="product-form-submit-error"
            className="bg-destructive/10 border-destructive/25 text-destructive mb-5 rounded-md border px-4 py-3 text-start text-sm"
          >
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden />
              <span>{t(submitError as TranslationKey)}</span>
            </div>
            {submitErrorNeedsBarcodeConfirmation ? (
              <div className="mt-3 space-y-2">
                {simpleBarcodeRow?.previousBarcodeNormalized ? (
                  <p>
                    {t('admin.products.barcodeConfirm.oldBarcode')}:{' '}
                    <bdi dir="ltr">{simpleBarcodeRow.previousBarcodeNormalized}</bdi>
                  </p>
                ) : null}
                {simpleBarcodeRow?.barcodeValue ? (
                  <p>
                    {t('admin.products.barcodeConfirm.newBarcode')}:{' '}
                    <bdi dir="ltr">{simpleBarcodeRow.barcodeValue}</bdi>
                  </p>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={confirmSimpleBarcodeChange}
                  data-testid="barcode-change-confirm"
                >
                  {t('admin.products.barcodeConfirm.confirm')}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* 1. Images ------------------------------------------------ */}
        <div className="mb-6">
          <Label className="text-foreground mb-1.5 block text-start text-sm font-medium">
            {t('admin.products.form.imagesLabel')}
          </Label>
          <MultiImageUpload
            value={slots}
            onChange={handleSlotsChange}
            disabled={isSubmitting}
            testIdPrefix="product-image-upload"
          />
          <p className="text-muted-foreground mt-2 text-start text-xs">
            {t('admin.products.form.imagesHint')}
          </p>
        </div>

        {/* 2. Name -------------------------------------------------- */}
        <div className="mb-5">
          <Label
            htmlFor="product-name"
            className="text-foreground mb-1.5 block text-start text-sm font-medium"
          >
            {t('admin.products.form.nameLabel')}
            <span className="text-terracotta ms-1" aria-hidden>
              *
            </span>
          </Label>
          <Input
            id="product-name"
            type="text"
            autoComplete="off"
            placeholder={t('admin.products.form.namePlaceholder')}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'product-name-error' : undefined}
            data-testid="product-name"
            maxLength={200}
            {...register('name')}
          />
          {errors.name ? (
            <p
              id="product-name-error"
              role="alert"
              className="text-destructive mt-1 text-start text-xs"
              data-testid="product-name-error"
            >
              {t(errors.name.message as TranslationKey)}
            </p>
          ) : null}
        </div>

        {/* 3. Category ---------------------------------------------- */}
        <div className="mb-5">
          <Label
            htmlFor="product-category"
            className="text-foreground mb-1.5 block text-start text-sm font-medium"
          >
            {t('admin.products.form.categoryLabel')}
            <span className="text-terracotta ms-1" aria-hidden>
              *
            </span>
          </Label>
          {categories.length > 0 ? (
            <div className="relative">
              <select
                id="product-category"
                className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-10 w-full appearance-none rounded-md border bg-none px-3 py-2 pe-10 text-start text-base [-moz-appearance:none] [-webkit-appearance:none] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                disabled={isSubmitting || isLoadingCategories}
                aria-invalid={!!errors.categoryId}
                aria-describedby={
                  errors.categoryId ? 'product-category-error' : 'product-category-hint'
                }
                data-testid="product-category"
                {...register('categoryId')}
              >
                <option value="">{t('admin.products.form.categoryPlaceholder')}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="text-muted-foreground pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2"
                aria-hidden
              />
            </div>
          ) : (
            <div className="border-border bg-admin-surface-muted rounded-lg border p-4 text-start">
              <p className="text-foreground text-sm font-medium">
                {t('admin.products.form.noCategoriesTitle')}
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                {t('admin.products.form.noCategoriesDescription')}
              </p>
              <Link
                to="/categories"
                className="text-zaatar hover:text-zaatar-deep mt-3 inline-block text-sm font-medium underline"
                data-testid="product-category-create-link"
              >
                {t('admin.products.form.noCategoriesCta')}
              </Link>
              <input type="hidden" {...register('categoryId')} />
            </div>
          )}
          {errors.categoryId ? (
            <p
              id="product-category-error"
              role="alert"
              className="text-destructive mt-1 text-start text-xs"
              data-testid="product-category-error"
            >
              {t(errors.categoryId.message as TranslationKey)}
            </p>
          ) : (
            <p
              id="product-category-hint"
              className="text-muted-foreground mt-1 text-start text-xs"
              data-testid="product-category-hint"
            >
              {t(
                isLoadingCategories
                  ? 'admin.products.form.categoryLoading'
                  : 'admin.products.form.categoryHint',
              )}
            </p>
          )}
        </div>

        {/* 4. Description ------------------------------------------- */}
        <div className="mb-5">
          <Label
            htmlFor="product-description"
            className="text-foreground mb-1.5 block text-start text-sm font-medium"
          >
            {t('admin.products.form.descriptionLabel')}
          </Label>
          <Textarea
            id="product-description"
            rows={4}
            maxLength={2000}
            placeholder={t('admin.products.form.descriptionPlaceholder')}
            aria-invalid={!!errors.description}
            aria-describedby={errors.description ? 'product-description-error' : undefined}
            data-testid="product-description"
            {...register('description')}
          />
          {errors.description ? (
            <p
              id="product-description-error"
              role="alert"
              className="text-destructive mt-1 text-start text-xs"
              data-testid="product-description-error"
            >
              {t(errors.description.message as TranslationKey)}
            </p>
          ) : null}
        </div>

        {/* 5. Product mode ------------------------------------------ */}
        <section
          className="border-border bg-admin-surface-muted mb-5 rounded-lg border p-4"
          aria-labelledby="product-mode-section-title"
        >
          <h2
            id="product-mode-section-title"
            className="text-foreground mb-3 text-start text-sm font-semibold"
          >
            {t('admin.products.form.modeSectionTitle')}
          </h2>
          <div className="grid gap-2 sm:grid-cols-2" role="radiogroup">
            {(['simple', 'options'] as const).map((candidate) => (
              <button
                key={candidate}
                type="button"
                role="radio"
                aria-checked={productMode === candidate}
                disabled={isSubmitting}
                onClick={() => setProductMode(candidate)}
                className={`rounded-md border px-4 py-3 text-start text-sm transition ${
                  productMode === candidate
                    ? 'border-zaatar bg-zaatar/10 text-foreground'
                    : 'border-border bg-card text-muted-foreground hover:bg-admin-surface'
                }`}
                data-testid={`product-mode-${candidate}`}
              >
                <span className="block font-medium">
                  {t(`admin.products.form.mode.${candidate}.label` as TranslationKey)}
                </span>
                <span className="mt-1 block text-xs">
                  {t(`admin.products.form.mode.${candidate}.hint` as TranslationKey)}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* 6. Price ------------------------------------------------- */}
        <div className="mb-5">
          <Label
            htmlFor="product-price"
            className="text-foreground mb-1.5 block text-start text-sm font-medium"
          >
            {t('admin.products.form.priceLabel')}
            <span className="text-terracotta ms-1" aria-hidden>
              *
            </span>
          </Label>
          <Controller
            control={control}
            name="priceRaw"
            render={({ field }) => (
              <PriceInput
                id="product-price"
                name={field.name}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="0.00"
                errorMessage={errors.priceRaw?.message ?? undefined}
                testId="product-price"
                aria-describedby={errors.priceRaw ? 'product-price-error' : 'product-price-hint'}
              />
            )}
          />
          {errors.priceRaw ? (
            <p
              id="product-price-error"
              role="alert"
              className="text-destructive mt-1 text-start text-xs"
              data-testid="product-price-error"
            >
              {t(errors.priceRaw.message as TranslationKey)}
            </p>
          ) : (
            <p
              id="product-price-hint"
              className="text-muted-foreground mt-1 text-start text-xs"
              data-testid="product-price-hint"
            >
              {t('admin.products.form.priceHint')}
            </p>
          )}
        </div>

        {/* 7. Sale -------------------------------------------------- */}
        <section
          className="border-border bg-admin-surface-muted mb-5 rounded-lg border p-4"
          aria-labelledby="product-sale-section-title"
        >
          <h2
            id="product-sale-section-title"
            className="text-foreground mb-3 text-start text-sm font-semibold"
          >
            {t('admin.products.form.saleSectionTitle')}
          </h2>

          <Label
            htmlFor="product-sale-enabled"
            className="flex cursor-pointer items-start gap-3 text-start text-sm font-medium"
          >
            <input
              id="product-sale-enabled"
              type="checkbox"
              className="border-border text-zaatar focus-visible:ring-zaatar mt-0.5 h-4 w-4 shrink-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isSubmitting}
              data-testid="product-sale-enabled"
              {...register('saleEnabled')}
            />
            <span className="text-foreground">{t('admin.products.form.saleEnabledLabel')}</span>
          </Label>

          {saleEnabled ? (
            <div className="mt-4">
              <Label
                htmlFor="product-sale-price"
                className="text-foreground mb-1.5 block text-start text-sm font-medium"
              >
                {t('admin.products.form.salePriceLabel')}
              </Label>
              <Controller
                control={control}
                name="salePriceRaw"
                render={({ field }) => (
                  <PriceInput
                    id="product-sale-price"
                    name={field.name}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="0.00"
                    errorMessage={errors.salePriceRaw?.message ?? undefined}
                    testId="product-sale-price"
                    aria-describedby={
                      errors.salePriceRaw ? 'product-sale-price-error' : 'product-sale-price-hint'
                    }
                  />
                )}
              />
              {errors.salePriceRaw ? (
                <p
                  id="product-sale-price-error"
                  role="alert"
                  className="text-destructive mt-1 text-start text-xs"
                  data-testid="product-sale-price-error"
                >
                  {t(errors.salePriceRaw.message as TranslationKey)}
                </p>
              ) : (
                <p
                  id="product-sale-price-hint"
                  className="text-muted-foreground mt-1 text-start text-xs"
                  data-testid="product-sale-price-hint"
                >
                  {t('admin.products.form.salePriceHint')}
                </p>
              )}
            </div>
          ) : null}
        </section>

        {/* 8. Inventory --------------------------------------------- */}
        <div className="mb-2">
          <Label
            htmlFor="product-inventory"
            className="text-foreground mb-1.5 block text-start text-sm font-medium"
          >
            {t('admin.products.form.inventoryLabel')}
          </Label>
          <Input
            id="product-inventory"
            type="number"
            inputMode="numeric"
            dir="ltr"
            min={0}
            step={1}
            placeholder="0"
            className="text-start"
            aria-invalid={!!errors.inventory}
            aria-describedby={
              errors.inventory ? 'product-inventory-error' : 'product-inventory-hint'
            }
            data-testid="product-inventory"
            {...register('inventory', {
              // An empty input maps to 0 per design §11 "default 0"; a
              // typed value is coerced to Number so Zod's .int() check
              // works. Non-numeric strings become NaN → "invalid".
              setValueAs: (v: unknown) => {
                if (v === '' || v === null || v === undefined) return 0;
                const n = Number(v);
                return Number.isFinite(n) ? n : Number.NaN;
              },
            })}
          />
          {errors.inventory ? (
            <p
              id="product-inventory-error"
              role="alert"
              className="text-destructive mt-1 text-start text-xs"
              data-testid="product-inventory-error"
            >
              {t(errors.inventory.message as TranslationKey)}
            </p>
          ) : (
            <p
              id="product-inventory-hint"
              className="text-muted-foreground mt-1 text-start text-xs"
              data-testid="product-inventory-hint"
            >
              {t('admin.products.form.inventoryHint')}
            </p>
          )}
        </div>

        {productMode === 'simple' ? (
          <div className="mb-5 mt-5">
            <Label
              htmlFor="product-barcode"
              className="text-foreground mb-1.5 block text-start text-sm font-medium"
            >
              {t('admin.products.form.barcodeLabel')}
            </Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="product-barcode"
                type="text"
                dir="ltr"
                autoComplete="off"
                autoCapitalize="off"
                className="text-start"
                placeholder={t('admin.products.form.barcodePlaceholder')}
                aria-invalid={!!errors.variants?.[0]?.barcodeValue}
                aria-describedby={
                  errors.variants?.[0]?.barcodeValue
                    ? 'product-barcode-error'
                    : 'product-barcode-hint'
                }
                data-testid="product-simple-barcode"
                {...register('variants.0.barcodeValue', {
                  onChange: () => {
                    setValue('variants.0.barcodeReplaceConfirmed', false, {
                      shouldDirty: true,
                    });
                    setValue('variants.0.barcodeClearConfirmed', false, {
                      shouldDirty: true,
                    });
                    setValue('variants.0.barcodeDeletedReuseConfirmedNormalized', null, {
                      shouldDirty: true,
                    });
                  },
                })}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setBarcodeScannerTarget({ type: 'simple' })}
                disabled={isSubmitting}
                data-testid="product-simple-barcode-camera"
                aria-label={t('admin.products.form.barcodeCameraAriaLabel')}
              >
                <Camera className="me-2 size-4" aria-hidden />
                {t('admin.barcode.scan.cameraOpen')}
              </Button>
            </div>
            <input type="hidden" {...register('variants.0.previousBarcodeNormalized')} />
            <input
              type="hidden"
              {...register('variants.0.barcodeDeletedReuseConfirmedNormalized')}
            />
            {errors.variants?.[0]?.barcodeValue ? (
              <div id="product-barcode-error" role="alert" className="mt-1 text-start">
                <p className="text-destructive text-xs">{t(simpleBarcodeErrorKey!)}</p>
                {simpleBarcodeNeedsConfirmation ? (
                  <div className="mt-2 space-y-2">
                    {simpleBarcodeRow?.previousBarcodeNormalized ? (
                      <p className="text-muted-foreground text-xs">
                        {t('admin.products.barcodeConfirm.oldBarcode')}:{' '}
                        <bdi dir="ltr">{simpleBarcodeRow.previousBarcodeNormalized}</bdi>
                      </p>
                    ) : null}
                    {simpleBarcodeRow?.barcodeValue ? (
                      <p className="text-muted-foreground text-xs">
                        {t('admin.products.barcodeConfirm.newBarcode')}:{' '}
                        <bdi dir="ltr">{simpleBarcodeRow.barcodeValue}</bdi>
                      </p>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={confirmSimpleBarcodeChange}
                      data-testid="barcode-change-confirm"
                    >
                      {t('admin.products.barcodeConfirm.confirm')}
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : (
              <p
                id="product-barcode-hint"
                className="text-muted-foreground mt-1 text-start text-xs"
              >
                {t('admin.products.form.barcodeHint')}
              </p>
            )}
          </div>
        ) : null}

        {productMode === 'options' ? (
          <section
            className="border-border bg-card mt-6 rounded-lg border p-4"
            aria-labelledby="product-variants-section-title"
            data-testid="product-variants-builder"
          >
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="text-start">
                <h2
                  id="product-variants-section-title"
                  className="text-foreground text-sm font-semibold"
                >
                  {t('admin.products.form.variantsTitle')}
                </h2>
                <p className="text-muted-foreground mt-1 text-xs">
                  {t('admin.products.form.variantsHint')}
                </p>
              </div>
            </div>

            <div
              className="border-border bg-background mb-4 rounded-lg border p-3"
              data-testid="product-variant-templates"
            >
              <p className="text-foreground text-start text-xs font-semibold">
                {t('admin.products.form.templates.title')}
              </p>
              {activeTemplateId ? (
                <p
                  className="text-muted-foreground mt-1 text-start text-xs"
                  data-testid="product-active-template"
                >
                  {t('admin.products.form.templates.active', {
                    template: t(
                      `admin.products.form.templates.${activeTemplateId}.label` as TranslationKey,
                    ),
                  })}
                </p>
              ) : null}
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {VARIANT_TEMPLATE_IDS.map((templateId) => {
                  const isActiveTemplate = activeTemplateId === templateId;

                  return (
                    <button
                      key={templateId}
                      type="button"
                      className={`border-border bg-card hover:bg-admin-surface focus-visible:ring-ring rounded-md border px-3 py-2 text-start text-xs transition focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                        isActiveTemplate
                          ? 'border-primary bg-primary/10 ring-primary/70 ring-2'
                          : ''
                      }`}
                      disabled={isSubmitting}
                      onClick={() => applyVariantTemplate(templateId)}
                      data-testid={`product-template-${templateId}`}
                      aria-pressed={isActiveTemplate}
                    >
                      <span className="flex items-start justify-between gap-2">
                        <span className="text-foreground block font-medium">
                          {t(`admin.products.form.templates.${templateId}.label` as TranslationKey)}
                        </span>
                        {isActiveTemplate ? (
                          <span
                            className="bg-primary text-primary-foreground inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold"
                            data-testid={`product-template-${templateId}-selected`}
                          >
                            <Check className="size-3" aria-hidden />
                            {t('admin.products.form.templates.selected')}
                          </span>
                        ) : null}
                      </span>
                      <span className="text-muted-foreground mt-0.5 block">
                        {t(`admin.products.form.templates.${templateId}.hint` as TranslationKey)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mb-4 flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={addOption}
                disabled={isSubmitting || optionFields.length >= 3}
                data-testid="product-add-option"
              >
                <Plus className="me-2 size-4" aria-hidden />
                {t('admin.products.form.addOption')}
              </Button>
            </div>

            <div className="space-y-4">
              {optionFields.map((optionField, optionIndex) => {
                const values = optionValues?.[optionIndex]?.values ?? [];
                const optionError = errors.options?.[optionIndex];
                const canRemoveOptionGroup =
                  activeTemplateId === 'custom' && optionFields.length > 1;

                return (
                  <div
                    key={optionField.fieldId}
                    className="border-border bg-admin-surface-muted rounded-lg border p-4"
                    data-testid={`product-option-${optionIndex}`}
                  >
                    <div className="text-muted-foreground mb-3 text-start text-xs font-semibold">
                      {t('admin.products.form.optionGroupLabel', { number: optionIndex + 1 })}
                    </div>
                    <input
                      type="hidden"
                      defaultValue={optionField.id}
                      {...register(`options.${optionIndex}.id`)}
                    />
                    <input
                      type="hidden"
                      defaultValue={optionIndex}
                      {...register(`options.${optionIndex}.displayOrder`, {
                        valueAsNumber: true,
                      })}
                    />
                    <div className="mb-3 flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <Label
                          htmlFor={`product-option-${optionIndex}-name`}
                          className="text-foreground mb-1.5 block text-start text-xs font-medium"
                        >
                          {t('admin.products.form.optionNameLabel')}
                        </Label>
                        <Input
                          id={`product-option-${optionIndex}-name`}
                          type="text"
                          disabled={isSubmitting}
                          placeholder={t('admin.products.form.optionNamePlaceholder')}
                          data-testid={`product-option-${optionIndex}-name`}
                          aria-invalid={!!optionError?.name}
                          {...register(`options.${optionIndex}.name`)}
                        />
                        {optionError?.name ? (
                          <p
                            role="alert"
                            className="text-destructive mt-1 text-start text-xs"
                            data-testid={`product-option-${optionIndex}-name-error`}
                          >
                            {t(optionError.name.message as TranslationKey)}
                          </p>
                        ) : null}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => removeOption(optionIndex)}
                        disabled={isSubmitting || !canRemoveOptionGroup}
                        aria-label={t('admin.products.form.removeOption')}
                        data-testid={`product-option-${optionIndex}-remove`}
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {values.map((value, valueIndex) => {
                        const valueError = optionError?.values?.[valueIndex]?.value;
                        return (
                          <div
                            key={value.id ?? valueIndex}
                            className="flex items-start gap-2"
                            data-testid={`product-option-${optionIndex}-value-${valueIndex}`}
                          >
                            <input
                              type="hidden"
                              defaultValue={value.id}
                              {...register(`options.${optionIndex}.values.${valueIndex}.id`)}
                            />
                            <input
                              type="hidden"
                              defaultValue={valueIndex}
                              {...register(
                                `options.${optionIndex}.values.${valueIndex}.displayOrder`,
                                { valueAsNumber: true },
                              )}
                            />
                            <div className="min-w-0 flex-1">
                              <Input
                                type="text"
                                disabled={isSubmitting}
                                placeholder={t('admin.products.form.optionValuePlaceholder')}
                                data-testid={`product-option-${optionIndex}-value-${valueIndex}-input`}
                                aria-invalid={!!valueError}
                                {...register(`options.${optionIndex}.values.${valueIndex}.value`)}
                              />
                              {valueError ? (
                                <p
                                  role="alert"
                                  className="text-destructive mt-1 text-start text-xs"
                                  data-testid={`product-option-${optionIndex}-value-${valueIndex}-error`}
                                >
                                  {t(valueError.message as TranslationKey)}
                                </p>
                              ) : null}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => removeOptionValue(optionIndex, valueIndex)}
                              disabled={isSubmitting || values.length <= 1}
                              aria-label={t('admin.products.form.removeOptionValue')}
                              data-testid={`product-option-${optionIndex}-value-${valueIndex}-remove`}
                            >
                              <Trash2 className="size-4" aria-hidden />
                            </Button>
                          </div>
                        );
                      })}
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      className="mt-3"
                      onClick={() => addOptionValue(optionIndex)}
                      disabled={isSubmitting}
                      data-testid={`product-option-${optionIndex}-add-value`}
                    >
                      <Plus className="me-2 size-4" aria-hidden />
                      {t('admin.products.form.addOptionValue')}
                    </Button>
                  </div>
                );
              })}
            </div>

            {errors.options?.message ? (
              <p
                role="alert"
                className="text-destructive mt-3 text-start text-xs"
                data-testid="product-options-error"
              >
                {t(errors.options.message as TranslationKey)}
              </p>
            ) : null}

            <div className="mt-5 overflow-x-auto">
              <table className="w-full min-w-[760px] border-separate border-spacing-0 text-start text-sm">
                <thead>
                  <tr className="text-muted-foreground text-xs">
                    <th className="border-border border-b px-2 py-2 text-start font-medium">
                      {t('admin.products.form.variantColumn')}
                    </th>
                    <th className="border-border border-b px-2 py-2 text-start font-medium">
                      {t('admin.products.form.variantPriceColumn')}
                    </th>
                    <th className="border-border border-b px-2 py-2 text-start font-medium">
                      {t('admin.products.form.variantSaleColumn')}
                    </th>
                    <th className="border-border border-b px-2 py-2 text-start font-medium">
                      {t('admin.products.form.variantInventoryColumn')}
                    </th>
                    <th className="border-border border-b px-2 py-2 text-start font-medium">
                      {t('admin.products.form.variantBarcodeColumn')}
                    </th>
                    <th className="border-border border-b px-2 py-2 text-start font-medium">
                      {t('admin.products.form.variantActiveColumn')}
                    </th>
                    <th className="border-border border-b px-2 py-2 text-start font-medium">
                      {t('admin.products.form.variantImageColumn')}
                    </th>
                    <th className="border-border border-b px-2 py-2 text-start font-medium">
                      {t('admin.products.form.variantDefaultColumn')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {variantFields.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-muted-foreground px-2 py-6 text-center">
                        {t('admin.products.form.variantEmpty')}
                      </td>
                    </tr>
                  ) : (
                    variantFields.map((variantField, variantIndex) => {
                      const row = variantRows?.[variantIndex] ?? variantField;
                      const variantError = errors.variants?.[variantIndex];
                      const variantLabel =
                        row.optionSummary ||
                        t('admin.products.form.variantUnnamed', {
                          number: variantIndex + 1,
                        });
                      return (
                        <tr
                          key={variantField.fieldId}
                          data-testid={`product-variant-${variantIndex}`}
                        >
                          <td className="border-border border-b px-2 py-3 align-top">
                            <span className="text-foreground block max-w-56 text-start font-medium">
                              <bdi>{row.optionSummary}</bdi>
                            </span>
                          </td>
                          <td className="border-border border-b px-2 py-3 align-top">
                            <Controller
                              control={control}
                              name={`variants.${variantIndex}.priceRaw`}
                              render={({ field }) => (
                                <PriceInput
                                  id={`product-variant-${variantIndex}-price`}
                                  name={field.name}
                                  value={field.value ?? ''}
                                  onChange={field.onChange}
                                  onBlur={field.onBlur}
                                  placeholder="0.00"
                                  aria-label={t('admin.products.form.variantPriceAriaLabel', {
                                    variant: variantLabel,
                                  })}
                                  errorMessage={variantError?.priceRaw?.message ?? undefined}
                                  testId={`product-variant-${variantIndex}-price`}
                                />
                              )}
                            />
                            {variantError?.priceRaw ? (
                              <p className="text-destructive mt-1 text-start text-xs">
                                {t(variantError.priceRaw.message as TranslationKey)}
                              </p>
                            ) : null}
                          </td>
                          <td className="border-border border-b px-2 py-3 align-top">
                            <Label className="mb-2 flex items-center gap-2 text-xs">
                              <input
                                type="checkbox"
                                className="border-border text-zaatar focus-visible:ring-zaatar size-4 shrink-0 rounded"
                                disabled={isSubmitting}
                                data-testid={`product-variant-${variantIndex}-sale-enabled`}
                                aria-label={t('admin.products.form.variantSaleToggleAriaLabel', {
                                  variant: variantLabel,
                                })}
                                {...register(`variants.${variantIndex}.saleEnabled`)}
                              />
                              {t('admin.products.form.variantSaleToggle')}
                            </Label>
                            {row.saleEnabled ? (
                              <Controller
                                control={control}
                                name={`variants.${variantIndex}.salePriceRaw`}
                                render={({ field }) => (
                                  <PriceInput
                                    id={`product-variant-${variantIndex}-sale-price`}
                                    name={field.name}
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    placeholder="0.00"
                                    aria-label={t('admin.products.form.variantSalePriceAriaLabel', {
                                      variant: variantLabel,
                                    })}
                                    errorMessage={variantError?.salePriceRaw?.message ?? undefined}
                                    testId={`product-variant-${variantIndex}-sale-price`}
                                  />
                                )}
                              />
                            ) : null}
                            {variantError?.salePriceRaw ? (
                              <p className="text-destructive mt-1 text-start text-xs">
                                {t(variantError.salePriceRaw.message as TranslationKey)}
                              </p>
                            ) : null}
                          </td>
                          <td className="border-border border-b px-2 py-3 align-top">
                            <Input
                              type="number"
                              inputMode="numeric"
                              dir="ltr"
                              min={0}
                              step={1}
                              className="w-24 text-start"
                              disabled={isSubmitting}
                              data-testid={`product-variant-${variantIndex}-inventory`}
                              aria-invalid={!!variantError?.inventory}
                              aria-label={t('admin.products.form.variantInventoryAriaLabel', {
                                variant: variantLabel,
                              })}
                              {...register(`variants.${variantIndex}.inventory`, {
                                setValueAs: (v: unknown) => {
                                  if (v === '' || v === null || v === undefined) return 0;
                                  const n = Number(v);
                                  return Number.isFinite(n) ? n : Number.NaN;
                                },
                              })}
                            />
                            {variantError?.inventory ? (
                              <p className="text-destructive mt-1 text-start text-xs">
                                {t(variantError.inventory.message as TranslationKey)}
                              </p>
                            ) : null}
                          </td>
                          <td className="border-border border-b px-2 py-3 align-top">
                            <div className="flex w-56 items-center gap-2">
                              <Input
                                type="text"
                                dir="ltr"
                                autoComplete="off"
                                autoCapitalize="off"
                                className="min-w-0 text-start"
                                disabled={isSubmitting}
                                data-testid={`product-variant-barcode-${variantIndex}`}
                                aria-invalid={!!variantError?.barcodeValue}
                                aria-label={t('admin.products.form.variantBarcodeAriaLabel', {
                                  variant: variantLabel,
                                })}
                                {...register(`variants.${variantIndex}.barcodeValue`, {
                                  onChange: () => {
                                    setValue(
                                      `variants.${variantIndex}.barcodeReplaceConfirmed`,
                                      false,
                                      {
                                        shouldDirty: true,
                                      },
                                    );
                                    setValue(
                                      `variants.${variantIndex}.barcodeClearConfirmed`,
                                      false,
                                      {
                                        shouldDirty: true,
                                      },
                                    );
                                    setValue(
                                      `variants.${variantIndex}.barcodeDeletedReuseConfirmedNormalized`,
                                      null,
                                      {
                                        shouldDirty: true,
                                      },
                                    );
                                  },
                                })}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-10 shrink-0 px-3"
                                disabled={isSubmitting}
                                onClick={() =>
                                  setBarcodeScannerTarget({ type: 'variant', index: variantIndex })
                                }
                                data-testid={`product-variant-barcode-camera-${variantIndex}`}
                                aria-label={t('admin.products.form.variantBarcodeCameraAriaLabel', {
                                  variant: variantLabel,
                                })}
                              >
                                <Camera className="size-4" aria-hidden />
                              </Button>
                            </div>
                            <input
                              type="hidden"
                              {...register(`variants.${variantIndex}.previousBarcodeNormalized`)}
                            />
                            <input
                              type="hidden"
                              {...register(
                                `variants.${variantIndex}.barcodeDeletedReuseConfirmedNormalized`,
                              )}
                            />
                            {variantError?.barcodeValue ? (
                              <p className="text-destructive mt-1 text-start text-xs">
                                {t(variantError.barcodeValue.message as TranslationKey)}
                              </p>
                            ) : null}
                            {barcodeErrorNeedsConfirmation(
                              variantError?.barcodeValue?.message as string | undefined,
                            ) ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="mt-2"
                                onClick={() => confirmVariantBarcodeChange(variantIndex)}
                                data-testid={`product-variant-barcode-confirm-${variantIndex}`}
                              >
                                {t('admin.products.barcodeConfirm.confirm')}
                              </Button>
                            ) : null}
                          </td>
                          <td className="border-border border-b px-2 py-3 align-top">
                            <input
                              type="checkbox"
                              className="border-border text-zaatar focus-visible:ring-zaatar size-4 rounded"
                              disabled={isSubmitting}
                              data-testid={`product-variant-${variantIndex}-active`}
                              aria-label={t('admin.products.form.variantActiveAriaLabel', {
                                variant: variantLabel,
                              })}
                              {...register(`variants.${variantIndex}.active`)}
                            />
                          </td>
                          <td className="border-border border-b px-2 py-3 align-top">
                            <div className="relative w-40">
                              <select
                                className="border-input bg-background text-foreground focus-visible:ring-ring h-10 w-full appearance-none rounded-md border bg-none px-3 py-2 pe-10 text-start text-base [-moz-appearance:none] [-webkit-appearance:none] focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                                disabled={isSubmitting || existingImageOptions.length === 0}
                                data-testid={`product-variant-${variantIndex}-image`}
                                aria-label={t('admin.products.form.variantImageAriaLabel', {
                                  variant: variantLabel,
                                })}
                                {...register(`variants.${variantIndex}.imageId`, {
                                  setValueAs: (value: unknown) =>
                                    typeof value === 'string' && value.trim() !== '' ? value : null,
                                })}
                              >
                                <option value="">
                                  {t('admin.products.form.variantImageNone')}
                                </option>
                                {existingImageOptions.map((image) => (
                                  <option key={image.id} value={image.id}>
                                    {image.label}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown
                                className="text-muted-foreground pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2"
                                aria-hidden="true"
                              />
                            </div>
                            {existingImageOptions.length === 0 ? (
                              <p className="text-muted-foreground mt-1 text-start text-xs">
                                {t('admin.products.form.variantImageUnavailable')}
                              </p>
                            ) : null}
                          </td>
                          <td className="border-border border-b px-2 py-3 align-top">
                            <input
                              type="radio"
                              name="product-default-variant"
                              className="border-border text-zaatar focus-visible:ring-zaatar size-4"
                              disabled={isSubmitting || !row.active}
                              checked={!!row.isDefault}
                              aria-label={t('admin.products.form.variantDefaultAriaLabel', {
                                variant: variantLabel,
                              })}
                              onChange={() => {
                                variantRows.forEach((variant, index) => {
                                  updateVariant(index, {
                                    ...variant,
                                    isDefault: index === variantIndex,
                                  });
                                });
                              }}
                              data-testid={`product-variant-${variantIndex}-default`}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {variantsArrayError ? (
              <p
                role="alert"
                className="text-destructive mt-3 text-start text-xs"
                data-testid="product-variants-error"
              >
                {t(variantsArrayError as TranslationKey)}
              </p>
            ) : null}
          </section>
        ) : null}
      </div>

      <BarcodeCameraScanDialog
        open={barcodeScannerTarget !== null}
        onOpenChange={(open) => {
          if (!open) setBarcodeScannerTarget(null);
        }}
        onDetected={applyScannedBarcode}
      />

      {/* --- Sticky action bar --------------------------------------
          Pinned to the bottom of the viewport per the Stitch design.
          `inset-inline-*` is logical so both LTR and RTL layouts
          anchor the bar full-width without directional classes. */}
      <div
        className="bg-background/95 border-border fixed inset-x-0 bottom-0 z-20 border-t backdrop-blur"
        dir={dir}
      >
        <div className="mx-auto flex max-w-screen-md items-center justify-between gap-3 px-6 py-4">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel ?? (() => undefined)}
            disabled={isSubmitting}
            data-testid="product-form-cancel"
          >
            {t('admin.products.form.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            aria-disabled={isSubmitting}
            data-testid="product-form-submit"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden />
                {t('admin.products.form.submitting')}
              </>
            ) : (
              t(submitLabelKey)
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
